/**
 * Shared helpers for the org-credential CLI commands (create-credential,
 * update-credential).
 */

import { expected } from './expected.js';

export const VALID_AUTH_TYPES = ['api_key', 'bearer', 'basic_auth'] as const;
export type AuthType = typeof VALID_AUTH_TYPES[number];

/** Maps legacy display names to canonical lowercase codes for backward compat. */
export const LEGACY_AUTH_TYPE_MAP: Record<string, AuthType> = {
  'API Key': 'api_key',
  'Bearer Token': 'bearer',
  'Basic Auth': 'basic_auth',
};

/** Normalize a user-supplied auth type, accepting legacy display names. */
export function normalizeCliAuthType(type: string): AuthType | null {
  const normalized = LEGACY_AUTH_TYPE_MAP[type] ?? type;
  return VALID_AUTH_TYPES.includes(normalized as AuthType) ? (normalized as AuthType) : null;
}

export const VALID_ENVIRONMENTS = ['preview', 'production', 'all'] as const;
export type Environment = typeof VALID_ENVIRONMENTS[number];

/** Validate a user-supplied credential environment; returns null when invalid. */
export function normalizeCliEnvironment(env: string): Environment | null {
  return VALID_ENVIRONMENTS.includes(env as Environment) ? (env as Environment) : null;
}

/**
 * Map a single secret string (read from stdin) to the credential body fields
 * the backend expects for the given auth type. Basic Auth expects
 * `username:password`. Throws on malformed Basic Auth input.
 */
export function mapSecretToCredentialFields(
  authType: AuthType,
  secret: string,
): Record<string, string> {
  switch (authType) {
    case 'bearer':
      return { access_token: secret };
    case 'api_key':
      return { api_key: secret };
    case 'basic_auth': {
      const colonIdx = secret.indexOf(':');
      if (colonIdx === -1) {
        throw new Error('Basic Auth stdin format: username:password');
      }
      return {
        username: secret.slice(0, colonIdx),
        password: secret.slice(colonIdx + 1),
      };
    }
  }
}

export interface OrgTagLike {
  tag_id: string;
  name: string;
}

/**
 * Resolve a list of tag references (names, or raw tag UUIDs) to tag UUIDs using
 * the org's tag registry. Accepts names primarily (the human-facing identifier);
 * a value that is already a known tag_id passes through. Unknown references throw
 * with the list of available tag names. Empty/whitespace entries are skipped.
 *
 * That refusal is `expected`-tagged: neither hub-side caller catches it
 * (`create-credential.ts`, `update-credential.ts`), so an unknown `--tags` value
 * reaches the CLI's top-level handler — and without the tag it would file a
 * Sentry crash for what is a typo (gh #4174). The Data surface reached the same
 * conclusion for `resolveTagNames` in `data/base-tags.ts`.
 */
export function resolveTagIds(tagRefs: string[], orgTags: OrgTagLike[]): string[] {
  const byName = new Map(orgTags.map((t) => [t.name, t.tag_id]));
  const byId = new Set(orgTags.map((t) => t.tag_id));
  const ids: string[] = [];
  const missing: string[] = [];

  for (const raw of tagRefs) {
    const ref = raw.trim();
    if (!ref) continue;
    const resolved = byName.get(ref);
    if (resolved) {
      ids.push(resolved);
    } else if (byId.has(ref)) {
      ids.push(ref);
    } else {
      missing.push(ref);
    }
  }

  if (missing.length > 0) {
    const available = orgTags.map((t) => t.name).join(', ') || '(none)';
    throw expected(
      `Unknown tag(s): ${missing.join(', ')}. Available org tags: ${available}`,
    );
  }

  // De-dup while preserving order.
  return [...new Set(ids)];
}

/**
 * Resolve tag references to tag NAMES — the form bases store them in
 * (docs/reference/tag-vocabulary.md §1.3), where hubs and credentials store UUIDs.
 *
 * The direction is the only difference from `resolveTagIds`, so it resolves
 * through that function rather than restating its acceptance (a name, or a raw
 * tag UUID already in the registry), its de-dup, and its refusal message — the
 * two must accept exactly the same references, and a second copy is where that
 * stops being true.
 */
export function resolveTagNames(tagRefs: string[], orgTags: OrgTagLike[]): string[] {
  const nameById = new Map(orgTags.map((t) => [t.tag_id, t.name]));
  // Every id here came out of `orgTags`, so the lookup cannot miss; the filter is
  // what makes that a type rather than an assertion.
  return resolveTagIds(tagRefs, orgTags)
    .map((id) => nameById.get(id))
    .filter((name): name is string => name !== undefined);
}

/**
 * Split repeatable + comma-separated `--tag` values into a flat list of refs.
 * `['a,b', 'c']` → `['a', 'b', 'c']`.
 */
export function splitTagRefs(rawValues: string[]): string[] {
  return rawValues
    .flatMap((v) => v.split(','))
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
}
